const calculateSimilarity = (path: string, inputPath: string) => {
  if (!path || path.trim().length === 0) {
    return -Infinity
  }

  const [basePath, queryString] = path.split('?')
  const [inputBasePath, inputQueryString] = inputPath.split('?')

  let score = 0

  // Handle exact base path match
  if (basePath === inputBasePath) {
    score += 20 // Strong match for exact base path
  } else if (inputBasePath.startsWith(basePath)) {
    score += 10 // Partial match for fallback base path (e.g., `/products` matches `/products/ipad`)
  } else {
    return -Infinity // Completely unrelated paths
  }

  const isSearch =
    queryString &&
    queryString?.includes('search=') &&
    inputQueryString?.includes('search=')

  if (isSearch) {
    const querySearchValue = new URLSearchParams(queryString).get('search')
    const inputSearchValue = new URLSearchParams(inputQueryString).get('search')

    // Handle generic fallback
    if (querySearchValue === '*') {
      score += 20 // Lower score for fallback
    } else if (
      querySearchValue?.toLowerCase() === inputSearchValue?.toLowerCase()
    ) {
      score += 50 // Higher score for exact search match (case-insensitive)
    } else if (querySearchValue && inputSearchValue) {
      score += 10 // Mild reward for partial match
    }
  }

  // If both have query strings, compare them
  if (queryString || inputQueryString) {
    const queryParams = new URLSearchParams(queryString || '')
    const inputParams = new URLSearchParams(inputQueryString || '')

    let matchedCount = 0
    let totalQueryParams = 0

    queryParams.forEach((value, key) => {
      totalQueryParams++
      const inputValue = inputParams.get(key)

      if (inputValue) {
        if (key === 'search') {
          // Case-insensitive comparison for `search`
          if (value.toLowerCase() === inputValue.toLowerCase()) {
            matchedCount++
          } else {
            score -= 1 // Mild penalty for mismatched `search` value
          }
        } else if (value === inputValue) {
          matchedCount++
        } else {
          score -= 1 // Mild penalty for mismatched value
        }
      } else {
        score -= 0.5 // Mild penalty for missing parameter
      }
    })

    // Handle extra parameters in the input path
    inputParams?.forEach((_value, key) => {
      if (!queryParams.has(key)) {
        totalQueryParams++
        score -= 0.5 // Mild penalty for extra parameter
      }
    })

    // Reward matches based on the proportion of matching parameters
    if (totalQueryParams > 0) {
      score += (matchedCount / totalQueryParams) * 5 // Add proportional reward
    }
  }

  // Boost score for an exact match of path and query parameters
  if (path === inputPath) {
    score += 10
  }

  return score
}

const findMostSimilarPath = (
  array: { pathname: string[] }[],
  inputPath: string
) => {
  // Remove language prefix (e.g., /en)
  const normalizedPath = inputPath.replace(/^\/en/, '').replace(/^\/th/, '')

  let bestMatch: number | null = null
  let bestScore = -1

  array?.forEach((group, index) => {
    group?.pathname?.forEach((path) => {
      if (!path || path.trim().length === 0) {
        return
      }

      const score = calculateSimilarity(path, normalizedPath)
      if (score > bestScore) {
        bestScore = score
        bestMatch = index
      }
    })
  })

  return bestMatch
}

export default findMostSimilarPath
